import { Injectable } from '@angular/core';
import { MapConstants } from '../../../utils/map.constant';
import * as L from 'leaflet';
import { delay } from 'q';
import 'leaflet.vectorgrid';
import 'src/assets/leaflet-velocity-src/js/L.CanvasLayer.js';
import 'src/assets/map-control/L.control.HtmlLegend.js';
import { ProjConstants } from 'src/app/utils/proj.constant';
import { HttpClient } from '@angular/common/http';
import { LightningDataService } from './lightning-data.service';


@Injectable({
  providedIn: 'root'
})
export class LightningMapService {

  stateBoundary: any;
  basinBoundary: any;

  constructor(
    private lightningDataService: LightningDataService,
    private http: HttpClient
  ) { }

  public createLayer(layer, gis) {
    if (layer.serviceType == 'WmsLayer') {
      return this.createWmsLayer(layer, gis);
    } else if (layer.serviceType == 'VectorLayer') {
      return this.createVectorLayer(layer, gis);
    } else if (layer.serviceType == 'GeoJsonTileLayer') {
      return this.createGeoJsonTileLayer(layer, gis);
    } else {
      return new Promise((resolve, reject) => { resolve(true); });
    }
  }

  createGeoJsonTileLayer(layer, gis) {
    const options = {
      key: 'API_KEY',
      pane: 'state'
    };
    const applicationOptions = {};
    applicationOptions['time_stamp'] = layer.timeStamp;
    applicationOptions['component_type'] = layer.componentType;
    applicationOptions['gis'] = gis;
    applicationOptions['component_measure_unit'] = layer.componentMeasureUnit;
    applicationOptions['color'] = layer.color;
    const layerPromise = new Promise((resolve, reject) => {
      layer['layer'] = new L.GridLayer.GeoJSON(layer.url, options, applicationOptions);
      resolve(layer);
    });
    return layerPromise;
  }

  createVectorLayer(layer, gis) {
    const options = {
      interactive: true,
      rendererFactory: {},
      opacity: 1,
      minZoom: 5,
      maxZoom: 18,
      pane: 'district',
      visible: true,
      transparent: true,
      vectorTileLayerStyles: {},
      getFeatureId: {}
    };
    options['rendererFactory'] = (L as any).canvas.tile;
    options['getFeatureId'] = function (feature) {
      return feature.properties.uuid;
    };
    options['vectorTileLayerStyles'][layer.layerName] = (properties, zoom) => {
      const style = {
        opacity: 0,
      };
      return style;
    };
    const url = MapConstants.GEOSERVER_WMTS_URL + '&layer=' +
      MapConstants.GEOSERVER_WORKSPACE_NAME + ':' + layer.layerName;
    layer['layer'] = (L as any).vectorGrid.protobuf(url, options);
    let dbClick = 0;
    gis.map.on({
      dblclick: (e: L.LayerEvent) => {
        {
          dbClick = 1;
        }
      }
    });
    if (layer.interactive) {
      layer['layer'].on({
        click: async (e: L.LayerEvent) => {
          {
            await delay(300);
            if (dbClick > 0) {
              await delay(1000);
              dbClick = 0;
              return;
            }
            const postData = {};
            postData['source'] = 'IMD_SHORT_RANGE';
            postData['latitude'] = e.latlng.lat;
            postData['longitude'] = e.latlng.lng;
            if (gis.adminSelected) {
              postData['view'] = 'Admin';
            } else {
              postData['view'] = 'Basin';
            }
            postData['forecastLength'] = gis.forecastLength;
            const apiParams = {};
            apiParams['url'] = 'BOTTOMBARDATA';
            apiParams['postData'] = postData;
            this.createPopUp(e.latlng, gis);
            gis.openBottomBar(apiParams, 'forecast');
            gis.selectedLocation = e.layer.properties[layer.locationName];
          }
        }
      });
    } else {
      layer['layer'].options.interactive = false;
    }
    return layer;
  }

  createWmsLayer(layer, gis) {
    const options = {
      layers: layer.layerName,
      opacity: 1,
      format: 'image/png',
      visible: true,
      transparent: true,
      pane: layer.zIndex ? layer.zIndex : 'district',
      fadeAnimation: false,
      attribution: '<a href = \'https://mausam.imd.gov.in/\'> Data Source : IMD </a>'
    };
    if (layer.minZoom != null && layer.minZoom != undefined) {
      options['minZoom'] = layer.minZoom;
    }
    if (layer.stateName != null && layer.stateName != undefined) {
      options['viewParams'] = 'stateName:' + layer.stateName;
    }
    if (layer.env != null && layer.env != undefined) {
      options['env'] = layer.env;
    }
    if (layer.cql_filter != null && layer.cql_filter != undefined) {
      options['cql_filter'] = layer.cql_filter;
    }
    if (layer.styles != null && layer.styles != undefined) {
      options['styles'] = layer.styles;
    }
    const url = MapConstants.GEOSERVER_WMS_URL;
    layer['layer'] = L.tileLayer.wms(url, options);
    return layer;
  }

  getImageBounds(imageBounds) {
    const anchors = [];
    anchors.push([imageBounds[1][0], imageBounds[0][1]]);
    anchors.push([imageBounds[1][0], imageBounds[1][1]]);
    anchors.push([imageBounds[0][0], imageBounds[1][1]]);
    anchors.push([imageBounds[0][0], imageBounds[0][1]]);
    return anchors;
  }

  getShapeBoundaryLayers(view, clip, stateName) {
    const shapeBoundary = new Promise((resolve, reject) => {
      if (clip == false) {
        resolve(null);
      }
      if (view == 'Admin') {
        if (this.stateBoundary == null || this.stateBoundary == undefined) {
          const layerName = MapConstants.GEOSERVER_WORKSPACE_NAME + ':' + ProjConstants.STATE_TO_LAYERTYPE_NAME[stateName + '_STATE_BOUNDARY'];
          const boundaryURL = MapConstants.GEOJSON_LAYER_URL + '&typeName=' + layerName + '&viewParams=stateName:' + stateName;
          this.http.get(boundaryURL).subscribe(
            data => {
              this.stateBoundary = data['features'][0]['geometry'];
              resolve(this.stateBoundary);
            });
        } else {
          resolve(this.stateBoundary);
        }
      } else {
        if (this.basinBoundary == null || this.basinBoundary == undefined) {

          const layerName = MapConstants.GEOSERVER_WORKSPACE_NAME + ':' + ProjConstants.STATE_TO_LAYERTYPE_NAME[stateName + '_BASIN_BOUNDARY'];
          const boundaryURL = MapConstants.GEOJSON_LAYER_URL + '&typeName=' + layerName + '&viewParams=stateName:' + ProjConstants.STATENAME;
          this.http.get(boundaryURL).subscribe(
            data => {
              this.basinBoundary = data['features'][0]['geometry'];
              resolve(this.basinBoundary);
            });
        } else {
          resolve(this.basinBoundary);
        }
      }
    });
    return shapeBoundary;
  }

  createPopUp(latlng, gis) {
    const myIcon = L.divIcon({ className: 'icon-dot', html: '<div class="pulsating-icon repeat"></div>' });
    const markerOptions = {
      icon: myIcon,
      pane: 'popUp'
    };
    if (gis.clickedLocationMarker != null && gis.clickedLocationMarker != undefined) {
      gis.clickedLocationMarker.unbindTooltip();
      gis.clickedLocationMarker.bindTooltip(gis.clickedLocationTooltip);
      gis.clickedLocationMarker = null;
    }
    if (gis.map.hasLayer(gis.marker)) {
      gis.map.removeLayer(gis.marker);
    }
    gis.marker = L.marker([latlng.lat, latlng.lng], markerOptions).addTo(gis.map);
  }

  createLightningMarker(gis) {
    const myIcon = L.divIcon({ className: 'icon-dot', html: '<div class="pulsating-icon repeat"></div>' });
    const markerOptions = {
      icon: myIcon,
      pane: 'popUp'
    };
    let data = [{'lat' : '10.5741575','lng' : '75.4183817'},
                {'lat' : '10.6074083','lng' : '75.4271868'},
              {'lat' : '10.6286533','lng' : '75.45428'},
            {'lat' : '8.7466731','lng' : '74.7965944'}]
    for(let i in data){
      gis.marker = L.marker([data[i].lat, data[i].lng], markerOptions).addTo(gis.map);
    }
  }

  async addTimeBasedLayers(gis, layerList) {
    gis.playButtonDisable = true;
    gis.labelStrings = [];
    const labels = [];
    let layerObjPromises = [];
    await delay(0.1);
    gis.showThrobber = true;
    for (const index in layerList) {
      if (index) {
        const layer = layerList[index];
        if (layer['type'] == 'Time' && layer['view'] == gis.view && layer['time'] == gis.forecastLength && layer['setDefault'] == 'true' && (layer['type2'] == undefined || layer['type2'] == gis.minMax)) {
          let layerObj: any;
          if (layer['layer'] != null && layer['layer'] != undefined) {
            layer['layer'].addTo(gis.map);
            gis.activeGroup = layer['groupName'];
            gis.activeLayers.push(layer);
            if (layer['timeLineName'] != null && layer['timeLineName'] != undefined) {
              labels[layer['timeLinePos']] = {
                groupName: layer['groupName'],
                label: layer['timeLineName'],
                fullLabel: layer['leftSideName'],
              };
            }
            layerObj = new Promise((resolve) => { resolve(layer); });
          } else {
            layerObj = this.createLayer(layer, gis);
            layerObj.then(
              data => {
                if (data['layer'] != undefined && data['layer'] != null) {
                  data['layer'].addTo(gis.map);
                  gis.activeGroup = data['groupName'];
                  gis.activeLayers.push(data);
                  if (layer['timeLineName'] != null && layer['timeLineName'] != undefined) {
                    labels[layer['timeLinePos']] = {
                      groupName: layer['groupName'],
                      label: layer['timeLineName'],
                      fullLabel: layer['leftSideName'],
                    };
                  }
                }
                layerList[index] = data;
              }
            );
          }
          layerObjPromises = layerObjPromises.concat(layerObj);
        }
      }
    }
    Promise.all(layerObjPromises).then(
      data => {
        gis.labelStrings = JSON.parse(JSON.stringify(labels));
        gis.playButtonDisable = false;
        gis.showThrobber = false;
      });
    let count: any = 1;
    let start = 0;
    let end = 4;
    while (count != 0) {
      await this.fetchTimeLayers(gis, count, start, end, layerList, labels).then(
        data => {
          count = data;
        });
      start = end;
      end = end + 4;
    }
    gis.initialCounter = parseInt(gis.labelStrings[0]['groupName'], 10);
  }

  fetchTimeLayers(gis, count, start, end, layerList, labels) {
    return new Promise((resolve, reject) => {
      count = 0;
      let layerObjPromises = [];
      for (const index in layerList) {
        if (index) {
          const layer = layerList[index];
          if (layer['type'] == 'Time' && layer['view'] == gis.view && layer['time'] == gis.forecastLength && layer['setDefault'] == 'false' && (layer['type2'] == undefined || layer['type2'] == gis.minMax)) {
            if ((layer['layer'] == undefined || layer['layer'] == null) && layer['timeLinePos'] >= start && layer['timeLinePos'] < end) {
              const layerObj = this.createLayer(layer, gis);
              layerObj.then(
                data => {
                  layerList[index] = data;
                  if (data['layer'] != null && data['layer'] != undefined) {
                    if (layer['timeLineName'] != null && layer['timeLineName'] != undefined) {
                      labels[layer['timeLinePos']] = {
                        groupName: layer['groupName'],
                        label: layer['timeLineName'],
                        fullLabel: layer['leftSideName'],
                      };
                    }
                  }
                  count++;
                }
              );
              layerObjPromises = layerObjPromises.concat(layerObj);
            } else if (layer['layer'] != null && layer['layer'] != undefined) {
              if (layer['timeLineName'] != null && layer['timeLineName'] != undefined) {
                labels[layer['timeLinePos']] = {
                  groupName: layer['groupName'],
                  label: layer['timeLineName'],
                  fullLabel: layer['leftSideName'],
                };
              }
            }
          }
        }
      }
      Promise.all(layerObjPromises).then(
        data => {
          gis.labelStrings = JSON.parse(JSON.stringify(labels));
          count = layerObjPromises.length;
          resolve(count);
        }
      );
    });
  }

  changeTimeLayers(gis, groupName) {
    if (groupName != gis.activeGroup) {
      const oldGroupName = gis.activeGroup;
      gis.activeGroup = groupName;
      this.removeGroupTimeLayers(gis, oldGroupName);
      this.addGroupTimeLayers(gis, groupName);
    }
  }

  addGroupTimeLayers(gis, groupName) {
    for (const index in gis.layerList) {
      if (index) {
        const layer = gis.layerList[index];
        if (layer['type'] == 'Time' && layer['time'] == gis.forecastLength && layer['groupName'] == groupName && layer['view'] == gis.view && (layer['type2'] == undefined || layer['type2'] == gis.minMax)) {
          if (layer['layer'] != null && layer['layer'] != undefined) {
            layer['layer'].addTo(gis.map);
            gis.activeLayers.push(layer);
          }
        }
      }
    }
  }

  removeGroupTimeLayers(gis, groupName) {
    for (const index in gis.activeLayers) {
      if (index) {
        const layer = gis.activeLayers[index];
        if (layer['groupName'] == groupName && layer['type'] == 'Time') {
          gis.map.removeLayer(layer['layer']);
          gis.activeLayers[index] = 'deleted';
        }
      }
    }
    gis.activeLayers = gis.activeLayers.filter(item => item != 'deleted');
  }

  removeAllTimeLayers(gis) {
    for (const index in gis.activeLayers) {
      if (index) {
        const layer = gis.activeLayers[index];
        if (layer['type'] == 'Time') {
          gis.map.removeLayer(layer['layer']);
          gis.activeLayers[index] = 'deleted';
        }
      }
    }
    gis.activeLayers = gis.activeLayers.filter(item => item != 'deleted');
  }

  getLightningMarkersData(){
    return this.http.get<any>("https://api.jsonbin.io/b/60b0df8c893b7c555b1d8fcf");
  }

}
